import { useState } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword } from '../../services/auth';
import { Mail, ArrowLeft, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await forgotPassword(email);
      setMessage(response.message || 'If an account exists, a reset link has been sent.');
    } catch (err) {
      // Even on error, we can show a generic message for security
      setMessage('If an account exists, a reset link has been sent.');
      // But we'll still log the error for debugging
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-split-container flex flex-col md:flex-row min-h-screen bg-slate-50">
      {/* LEFT SIDE: Brand & Welcome (hidden on mobile) */}
      <div className="auth-sidebar hidden md:flex md:w-2/5 bg-gradient-to-br from-nym-navy to-nym-blue text-white p-10 flex-col justify-between">
        <div>
          <div className="brand-badge inline-block bg-white/20 backdrop-blur-sm px-4 py-1 rounded-full text-xs font-bold tracking-wider mb-6">
            NYM Portal
          </div>
          <h1 className="text-4xl font-bold leading-tight">Reset Your Password</h1>
          <p className="text-white/80 mt-4 text-lg">
            Enter your registered email address and we'll send you a link to reset your password.
          </p>
        </div>
        <div className="auth-sidebar-footer text-xs opacity-50">
          © 2026 Friends Church (Quakers) NYM
        </div>
      </div>

      {/* RIGHT SIDE: The Form (full width on mobile) */}
      <div className="auth-form-section flex-1 bg-white md:bg-transparent p-6 md:p-12 flex items-center justify-center">
        <div className="login-form-wrapper w-full max-w-md mx-auto py-8 md:py-0">
          {/* Mobile logo */}
          <div className="md:hidden flex justify-center mb-6">
            <span className="text-2xl font-black text-nym-navy">NYM</span>
          </div>

          <h2 className="text-3xl font-black text-nym-navy mb-2 text-center md:text-left">
            Forgot Password?
          </h2>
          <p className="text-slate-500 mb-8 text-center md:text-left">
            No worries. We'll send you a reset link.
          </p>

          {/* Success message */}
          {message && (
            <div className="mb-6 flex items-start gap-3 p-4 bg-emerald-50 text-emerald-700 rounded-xl border border-emerald-100">
              <CheckCircle size={20} className="flex-shrink-0 mt-0.5" />
              <span className="text-sm font-medium">{message}</span>
            </div>
          )}

          {/* Error message (only shown if we actually want to show a real error) */}
          {error && (
            <div className="mb-6 flex items-start gap-3 p-4 bg-red-50 text-red-700 rounded-xl border border-red-100">
              <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
              <span className="text-sm font-medium">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="modern-form space-y-6">
            <div className="input-group">
              <label className="block text-sm font-bold text-slate-700 mb-1">Email Address</label>
              <div className="input-wrapper relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input
                  type="email"
                  placeholder="you@example.com"
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <button
              type="submit"
              className="nym-primary-btn w-full bg-nym-blue text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  <span>Sending...</span>
                </>
              ) : (
                <span>Send Reset Link</span>
              )}
            </button>
          </form>

          <div className="text-center mt-8 text-sm text-slate-500">
            <Link to="/login" className="inline-flex items-center gap-1 text-nym-blue font-bold hover:underline transition-colors">
              <ArrowLeft size={16} />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;